import { useNavigate } from "react-router-dom";
import {
  Loader2, TrendingUp, TrendingDown, Wallet, ShoppingCart, Inbox,
  AlertCircle, ArrowRight, BarChart3, ArrowUpRight, ArrowDownRight,
  Utensils, Bike, ShoppingBag,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import FinanceInsights from "./FinanceInsights";
import ExportForAccountantDialog from "./ExportForAccountantDialog";
import ExportQuarterForAccountantDialog from "./ExportQuarterForAccountantDialog";
import ExportAnnualForAccountantDialog from "./ExportAnnualForAccountantDialog";
import NextFiscalDeadlineCard from "./NextFiscalDeadlineCard";
import FinanceExportHistoryCard from "./FinanceExportHistoryCard";

const CHANNEL_META = {
  room:     { label: "Sala",     Icon: Utensils,    tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  delivery: { label: "Delivery", Icon: Bike,        tone: "text-sky-700 bg-sky-50 border-sky-200" },
  takeaway: { label: "Recoger",  Icon: ShoppingBag, tone: "text-amber-700 bg-amber-50 border-amber-200" },
  other:    { label: "Otros",    Icon: Wallet,      tone: "text-foreground bg-muted/50 border-border" },
};

export default function FinanceOverview({
  restaurantId, range, onRangeChange, summary, trend, loading, catLabels, fmtEUR, onNavigateTab,
}) {
  const navigate = useNavigate();
  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!summary) {
    return <Card className="border-dashed"><CardContent className="py-10 text-center text-sm text-muted-foreground">No hay datos para este periodo.</CardContent></Card>;
  }

  const deltas = summary.deltas || {};
  const kpis = [
    { key: "revenue", label: "Ingresos", value: fmtEUR(summary.revenue),
      sub: `${summary.ticket_count} tickets`, delta: deltas.revenue_pct,
      icon: TrendingUp, tone: "emerald", onClick: () => navigate("/app/analytics") },
    { key: "purchases", label: "Compras (food cost)", value: fmtEUR(summary.purchases),
      sub: summary.revenue > 0 ? `${summary.food_cost_pct}% sobre ventas` : "",
      delta: deltas.purchases_pct, deltaInvert: true,
      icon: ShoppingCart, tone: "amber", onClick: () => navigate("/app/inventory") },
    { key: "opex", label: "Gastos operativos", value: fmtEUR(summary.opex),
      sub: "Suministros, alquiler, nóminas…",
      delta: deltas.opex_pct, deltaInvert: true,
      icon: Wallet, tone: "sky", onClick: () => onNavigateTab("expenses") },
    { key: "profit", label: "Beneficio neto", value: fmtEUR(summary.net_profit),
      sub: summary.revenue > 0 ? `${summary.net_margin_pct}% margen` : "—",
      delta: deltas.net_profit_pct,
      icon: summary.net_profit >= 0 ? TrendingUp : TrendingDown,
      tone: summary.net_profit >= 0 ? "emerald" : "red", emphasis: true },
  ];

  const toneStyles = {
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900",
    amber:   "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    sky:     "from-sky-50 to-sky-100 border-sky-200 text-sky-900",
    red:     "from-red-50 to-red-100 border-red-200 text-red-900",
  };

  const byCat = summary.expenses_by_category || {};
  const catRows = Object.entries(byCat)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-5" data-testid="finance-overview">
      <NextFiscalDeadlineCard />
      {/* Range picker */}
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={range.from} onChange={(e) => onRangeChange({ ...range, from: e.target.value })} className="w-auto" data-testid="finance-from-date" />
        <span className="text-sm text-muted-foreground">→</span>
        <Input type="date" value={range.to} onChange={(e) => onRangeChange({ ...range, to: e.target.value })} className="w-auto" data-testid="finance-to-date" />
        <Button size="sm" variant="ghost" onClick={() => {
          const d = new Date();
          onRangeChange({
            from: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10),
            to: new Date().toISOString().slice(0, 10),
          });
        }}>Este mes</Button>
        <Button size="sm" variant="ghost" onClick={() => {
          const d = new Date();
          onRangeChange({
            from: new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().slice(0, 10),
            to: new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10),
          });
        }}>Mes pasado</Button>
        {summary.previous && (
          <span className="ml-auto text-[11px] text-muted-foreground">
            vs. {summary.previous.range.from} → {summary.previous.range.to}
          </span>
        )}
        <div className={summary.previous ? "flex gap-2 flex-wrap" : "ml-auto flex gap-2 flex-wrap"}>
          <ExportForAccountantDialog restaurantId={restaurantId} />
          <ExportQuarterForAccountantDialog restaurantId={restaurantId} />
          <ExportAnnualForAccountantDialog restaurantId={restaurantId} />
        </div>
      </div>

      {/* AI Insights card */}
      <FinanceInsights restaurantId={restaurantId} range={range} />
      <FinanceExportHistoryCard restaurantId={restaurantId} />

      {/* KPIs with delta chips */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map(k => {
          const Icon = k.icon;
          return (
            <button
              key={k.key}
              onClick={k.onClick}
              disabled={!k.onClick}
              className={`relative text-left rounded-2xl border bg-gradient-to-br ${toneStyles[k.tone]} p-4 shadow-sm ${k.onClick ? "hover:shadow-md cursor-pointer transition" : ""} ${k.emphasis ? "ring-2 ring-offset-1 ring-current/30" : ""}`}
              data-testid={`finance-kpi-${k.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className="h-5 w-5 opacity-80" />
                {k.onClick && <ArrowRight className="h-3.5 w-3.5 opacity-60" />}
              </div>
              <div className="text-2xl font-bold tracking-tight">{k.value}</div>
              <div className="text-xs opacity-80 mt-0.5">{k.label}</div>
              <div className="flex items-center justify-between mt-0.5">
                {k.sub && <span className="text-[11px] opacity-70">{k.sub}</span>}
                <DeltaChip value={k.delta} invert={k.deltaInvert} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Action alerts */}
      {(summary.pending_docs > 0 || summary.unpaid_count > 0) && (
        <div className="grid sm:grid-cols-2 gap-3">
          {summary.pending_docs > 0 && (
            <button onClick={() => onNavigateTab("inbox")} className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 hover:bg-amber-100 transition" data-testid="pending-docs-alert">
              <Inbox className="h-5 w-5 text-amber-700 shrink-0" />
              <div className="flex-1 text-left">
                <div className="text-sm font-semibold text-amber-900">{summary.pending_docs} documento{summary.pending_docs === 1 ? "" : "s"} por revisar</div>
                <div className="text-xs text-amber-800/80">Revisa y confirma los datos extraídos por IA</div>
              </div>
              <ArrowRight className="h-4 w-4 text-amber-700" />
            </button>
          )}
          {summary.unpaid_count > 0 && (
            <button onClick={() => onNavigateTab("inbox")} className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 hover:bg-red-100 transition" data-testid="unpaid-docs-alert">
              <AlertCircle className="h-5 w-5 text-red-700 shrink-0" />
              <div className="flex-1 text-left">
                <div className="text-sm font-semibold text-red-900">{fmtEUR(summary.unpaid_total)} sin pagar</div>
                <div className="text-xs text-red-800/80">{summary.unpaid_count} factura{summary.unpaid_count === 1 ? "" : "s"} pendiente{summary.unpaid_count === 1 ? "" : "s"} de pago</div>
              </div>
              <ArrowRight className="h-4 w-4 text-red-700" />
            </button>
          )}
        </div>
      )}

      {/* Revenue by channel + Trend chart */}
      <div className="grid lg:grid-cols-2 gap-3">
        <ChannelBreakdown summary={summary} fmtEUR={fmtEUR} />
        <TrendChart trend={trend} fmtEUR={fmtEUR} />
      </div>

      {/* Category breakdown */}
      {catRows.length > 0 && (
        <Card>
          <CardContent className="p-5 space-y-3">
            <h3 className="font-semibold text-sm text-foreground">Desglose por categoría</h3>
            <div className="space-y-1.5">
              {catRows.map(([cat, val]) => {
                const denom = (summary.purchases + summary.opex) || 0;
                const pct = denom > 0 ? (val / denom) * 100 : 0;
                return (
                  <div key={cat} className="space-y-1" data-testid={`category-row-${cat}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{catLabels[cat] || cat}</span>
                      <span className="font-medium">{fmtEUR(val)}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-primary/60 to-primary" style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Delta chip ───────────────────────────────────────────────────────

function DeltaChip({ value, invert = false }) {
  if (value === null || value === undefined) return <span />;
  const positive = value >= 0;
  // For expenses/cost KPIs, invert colour: growth is bad.
  const isGood = invert ? !positive : positive;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  const tone = isGood
    ? "text-emerald-800 bg-emerald-100 border-emerald-200"
    : "text-red-800 bg-red-100 border-red-200";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${tone}`}>
      <Icon className="h-2.5 w-2.5" />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

// ─── Channel breakdown ────────────────────────────────────────────────

function ChannelBreakdown({ summary, fmtEUR }) {
  const byCh = summary.revenue_by_channel || {};
  const total = Object.values(byCh).reduce((s, v) => s + v, 0);
  const rows = Object.entries(byCh)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <Card>
      <CardContent className="p-5 space-y-3" data-testid="channel-breakdown">
        <h3 className="font-semibold text-sm text-foreground flex items-center gap-1.5">
          <Utensils className="h-4 w-4" /> Ingresos por canal
        </h3>
        {total === 0 ? (
          <div className="text-sm text-muted-foreground py-3">Sin ingresos registrados en este periodo.</div>
        ) : (
          <>
            <div className="flex h-2 w-full rounded-full overflow-hidden bg-muted">
              {rows.map(([ch, v]) => {
                const pct = (v / total) * 100;
                const bg = {
                  room:     "bg-emerald-500",
                  delivery: "bg-sky-500",
                  takeaway: "bg-amber-500",
                  other:    "bg-gray-400",
                }[ch] || "bg-gray-400";
                return <div key={ch} className={bg} style={{ width: `${pct}%` }} title={`${CHANNEL_META[ch]?.label}: ${pct.toFixed(1)}%`} />;
              })}
            </div>
            <div className="space-y-1.5">
              {rows.map(([ch, v]) => {
                const meta = CHANNEL_META[ch] || CHANNEL_META.other;
                const { Icon } = meta;
                const pct = (v / total) * 100;
                return (
                  <div key={ch} className="flex items-center gap-2" data-testid={`channel-${ch}`}>
                    <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${meta.tone}`}>
                      <Icon className="h-3 w-3" /> {meta.label}
                    </span>
                    <div className="flex-1 text-xs text-muted-foreground">{pct.toFixed(1)}%</div>
                    <div className="text-sm font-medium">{fmtEUR(v)}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Trend chart (12 months, pure SVG) ────────────────────────────────

function TrendChart({ trend, fmtEUR }) {
  const months = trend?.months || [];
  const hasData = months.some(m => (m.revenue || 0) + (m.purchases || 0) + (m.opex || 0) > 0);

  const W = 420, H = 140, P = 18;
  const n = Math.max(months.length, 1);
  const bw = (W - 2 * P) / n;
  const maxRev = Math.max(1, ...months.map(m => m.revenue || 0));
  const yRev = (v) => H - P - ((v / maxRev) * (H - 2 * P));
  const profitPath = months
    .map((m, i) => {
      const x = P + bw * (i + 0.5);
      const y = yRev(m.net_profit);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <Card>
      <CardContent className="p-5 space-y-2" data-testid="trend-chart">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm text-foreground flex items-center gap-1.5">
            <BarChart3 className="h-4 w-4" /> Tendencia 12 meses
          </h3>
          {hasData && (
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-1.5 rounded bg-emerald-300" /> Ingresos</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 rounded bg-indigo-600" /> Beneficio</span>
            </div>
          )}
        </div>
        {!hasData ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Aún no hay movimientos históricos.</div>
        ) : (
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32">
            {/* Revenue bars */}
            {months.map((m, i) => {
              const x = P + bw * i + 2;
              const y = yRev(m.revenue);
              const h = Math.max(0, H - P - y);
              return (
                <rect key={i} x={x} y={y} width={bw - 4} height={h}
                      className="fill-emerald-300" rx="2">
                  <title>{m.month}: {fmtEUR(m.revenue)}</title>
                </rect>
              );
            })}
            {/* Profit line */}
            <path d={profitPath} className="stroke-indigo-600" fill="none" strokeWidth="1.5" />
            {/* Month labels (every other) */}
            {months.map((m, i) => (
              i % 2 === 0 && (
                <text key={`lbl-${i}`} x={P + bw * (i + 0.5)} y={H - 4}
                      className="fill-gray-400" fontSize="8" textAnchor="middle">
                  {m.month.slice(5)}
                </text>
              )
            ))}
          </svg>
        )}
      </CardContent>
    </Card>
  );
}
